import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell, BookOpen, Check, FileQuestion, KeyRound, Layers, Lock, Monitor, Moon, Save, Sparkles, Sun, Target, UserRound,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import { useTheme } from '../contexts/ThemeContext';
import { authService, apiErrorMessage } from '../services/api';
import { AppShell, Badge, Button, Card, Field, Loader, PageHeader, SectionTitle } from '../components/ui';
import { AccountAI, useAccountAI } from '../components/account/AccountAI';
import './Account.css';

const GOALS = [
  { key: 'lessons', field: 'goalLessonsWeek', label: 'Aulas por semana', icon: BookOpen, placeholder: 'Ex.: 5' },
  { key: 'questions', field: 'goalQuestionsWeek', label: 'Questões por semana', icon: FileQuestion, placeholder: 'Ex.: 100' },
  { key: 'flashcards', field: 'goalFlashcardsWeek', label: 'Flashcards por semana', icon: Layers, placeholder: 'Ex.: 70' },
];

const THEMES = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Igual ao sistema', icon: Monitor },
];

function Account() {
  const { user, isAdmin, setUser } = useAuth();
  const { courses } = useCourse();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const [goals, setGoals] = useState(() => Object.fromEntries(GOALS.map(g => [g.key, user[g.field] ?? ''])));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [params, setParams] = useSearchParams();
  const tab = params.get('aba') === 'ia' ? 'ia' : 'perfil';
  // A aba de IA só aparece quando o super admin permite chave própria
  const [ai, reloadAI] = useAccountAI(!user.demo);
  const showAI = ai && ai.allowed;

  const saveGoals = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const toNumber = (v) => (v === '' || v === null ? null : Number(v));
      const me = await authService.updateGoals({
        lessons: toNumber(goals.lessons), questions: toNumber(goals.questions), flashcards: toNumber(goals.flashcards),
      });
      setUser(me);
      setMessage({ ok: true, text: 'Metas salvas.' });
    } catch (err) {
      setMessage({ ok: false, text: apiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell section="Minha conta">
      <PageHeader icon={UserRound} eyebrow={user.username} title="Minha conta" subtitle="Seus dados, metas de estudo e preferências." />

      <div className="acc-layout">
        <nav className="acc-nav" aria-label="Seções da conta">
          <button type="button" className={tab === 'perfil' ? 'is-active' : ''} onClick={() => setParams({})}>
            <UserRound size={16} aria-hidden="true" />Perfil e metas
          </button>
          <button type="button" onClick={() => navigate('/notificacoes')}>
            <Bell size={16} aria-hidden="true" />Notificações
          </button>
          {showAI && (
            <button type="button" className={tab === 'ia' ? 'is-active' : ''} onClick={() => setParams({ aba: 'ia' })}>
              <Sparkles size={16} aria-hidden="true" />Inteligência artificial
            </button>
          )}
          {!user.demo && (
            <button type="button" onClick={() => navigate('/trocar-senha')}>
              <Lock size={16} aria-hidden="true" />Senha
            </button>
          )}
        </nav>
        <div className="acc-content">
          {tab === 'ia' ? (ai === null ? <Loader /> : showAI ? <AccountAI data={ai} onChange={reloadAI} /> : (
            <p className="acc-msg">O administrador não liberou chave própria.</p>
          )) : (
          <div className="acc-grid">
            <Card padding="lg" className="acc-profile">
              <span className="acc-avatar" aria-hidden="true">
                {user.name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()}
              </span>
              <div className="acc-profile__text">
                <strong>{user.name}</strong>
                <span>{user.email || 'Sem e-mail cadastrado'}</span>
              </div>
              <dl className="acc-facts">
                <div><dt>Login</dt><dd>{user.username}</dd></div>
                <div><dt>Papel</dt><dd><Badge tone={isAdmin ? 'solid' : 'navy'}>{isAdmin ? 'Admin' : 'Usuário'}</Badge></dd></div>
                <div>
                  <dt>Cursos</dt>
                  <dd className="acc-courses">
                    {isAdmin || user.allCourses
                      ? <Badge tone="lime">Todos os cursos</Badge>
                      : courses.map(c => <Badge key={c.id} tone="neutral">{c.name}</Badge>)}
                  </dd>
                </div>
              </dl>
              {!user.demo && (
                <Button variant="outline" icon={KeyRound} onClick={() => navigate('/trocar-senha')}>Trocar senha</Button>
              )}
            </Card>

            <div className="acc-side">
              <Card padding="lg">
                <div className="acc-card-head">
                  <Target size={20} aria-hidden="true" />
                  <div>
                    <h2>Metas semanais</h2>
                    <p>Usadas no painel e nos lembretes. Deixe em branco para não ter meta.</p>
                  </div>
                </div>
                <form onSubmit={saveGoals} className="acc-goals">
                  {GOALS.map(g => (
                    <Field key={g.key} label={g.label}>
                      <div className="acc-input-icon">
                        <g.icon size={16} aria-hidden="true" />
                        <input
                          type="number"
                          min="0"
                          max="10000"
                          inputMode="numeric"
                          placeholder={g.placeholder}
                          value={goals[g.key]}
                          onChange={(e) => setGoals({ ...goals, [g.key]: e.target.value })}
                        />
                      </div>
                    </Field>
                  ))}
                  <div className="acc-goals__foot">
                    {message && (
                      <span className={message.ok ? 'acc-msg acc-msg--ok' : 'acc-msg acc-msg--bad'}>
                        {message.ok && <Check size={14} aria-hidden="true" />} {message.text}
                      </span>
                    )}
                    <Button type="submit" icon={Save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar metas'}</Button>
                  </div>
                </form>
              </Card>

              <SectionTitle>Aparência</SectionTitle>
              <div className="acc-themes" role="radiogroup" aria-label="Tema">
                {THEMES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={preference === t.value}
                    className={`acc-theme ${preference === t.value ? 'is-on' : ''}`}
                    onClick={() => setPreference(t.value)}
                  >
                    <t.icon size={20} aria-hidden="true" />
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export default Account;
